import { useEffect, useRef, useState } from 'react';
import { customerKycFields, refreshCustomerKyc, submitCustomerKyc, validCustomerKycDraft } from './customer-kyc.js';
import { customerKycFailure, isCustomerKycTerminal } from './customer-kyc-recovery.js';
/** Mount keyed by customer ID. PII is never persisted or placed in the query cache. */
export function useCustomerKyc(provider, initial, onComplete, active = true) {
  const [verification, setVerification] = useState(initial);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const wasActive = useRef(active);
  const completionReported = useRef(false);
  const lifetime = useRef({
    active: true,
    pending: false
  });
  useEffect(() => {
    const scope = {
      active: true,
      pending: false
    };
    lifetime.current = scope;
    return () => {
      scope.active = false;
    };
  }, []);
  async function execute(submit) {
    const scope_0 = lifetime.current;
    if (!scope_0.active || scope_0.pending || submit && isCustomerKycTerminal(verification.status, error)) return false;
    scope_0.pending = true;
    setBusy(true);
    setError(null);
    try {
      const next = submit ? await submitCustomerKyc(provider, verification, draft) : await refreshCustomerKyc(provider, initial.customerId);
      if (!scope_0.active) return false;
      setDraft(previous => submit ? {} : remainingDraft(previous, next));
      setVerification(next);
      return true;
    } catch (cause) {
      if (scope_0.active) setError(customerKycFailure(cause));
      return false;
    } finally {
      scope_0.pending = false;
      if (scope_0.active) setBusy(false);
    }
  }
  // The documented Customer API example polls every three seconds while verifying.
  useEffect(() => {
    const resumed = active && !wasActive.current;
    wasActive.current = active;
    if (!active || !resumed && (verification.status !== 'verifying' || verification.challenge || error)) return;
    let current = true;
    function read() {
      const scope_1 = lifetime.current;
      if (!scope_1.active || scope_1.pending) return;
      scope_1.pending = true;
      void refreshCustomerKyc(provider, initial.customerId).then(next_0 => {
        if (current && scope_1.active) {
          setVerification(next_0);
          setDraft(previous_0 => remainingDraft(previous_0, next_0));
          setError(null);
        }
      }).catch(cause_0 => {
        if (current && scope_1.active) setError(customerKycFailure(cause_0));
      }).finally(() => {
        scope_1.pending = false;
      });
    }
    if (resumed) read();
    const timer = verification.status === 'verifying' && !verification.challenge && !error ? setInterval(read, 3000) : null;
    return () => {
      current = false;
      if (timer !== null) clearInterval(timer);
    };
  }, [active, provider, initial.customerId, verification.status, verification.challenge, error]);
  const {
    fields,
    unsupported
  } = customerKycFields(verification);
  return {
    verification,
    fields,
    draft,
    unsupported,
    busy,
    error,
    canSubmit: verification.status === 'collecting' && !isCustomerKycTerminal(verification.status, error) && !verification.challenge && validCustomerKycDraft(fields, draft) && !busy,
    setField: (field, value) => {
      setDraft(previous_1 => ({
        ...previous_1,
        [field]: value
      }));
    },
    submit: async () => {
      await execute(true);
    },
    refresh: () => execute(false),
    continuePayment: () => {
      if (!lifetime.current.active || lifetime.current.pending || busy || error || verification.status !== 'active' || verification.challenge || completionReported.current) return;
      completionReported.current = true;
      onComplete();
    }
  };
}
function remainingDraft(draft, verification) {
  if (verification.status !== 'collecting' || verification.challenge) return {};
  return Object.fromEntries(customerKycFields(verification).fields.filter(field => draft[field] !== undefined).map(field => [field, draft[field]]));
}